/** The slice of `Storage` the OAuth state needs; `sessionStorage` satisfies it. */
export type OAuthStateStorage = Pick<
  Storage,
  "getItem" | "setItem" | "removeItem"
>;

export type OAuthProvider = "google" | "microsoft";

const keyFor = (provider: OAuthProvider) => `oauthState:${provider}`;

/**
 * This tab's session storage, or null where there is none (native, SSR) or the browser
 * refuses it (private modes): without it no state can be proven, so no login completes.
 */
export const sessionStateStorage = (): OAuthStateStorage | null => {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
};

/** Remembers the state a login redirect carries, to recognise it when it comes back. */
export const saveOAuthState = (
  provider: OAuthProvider,
  state: string,
  storage: OAuthStateStorage | null = sessionStateStorage(),
): void => {
  try {
    storage?.setItem(keyFor(provider), state);
  } catch {
    // Without storage the callback cannot match the state and refuses the login.
  }
};

/**
 * Whether the state a provider echoed back is the one this tab sent, so the callback
 * only honours logins this tab started (an attacker's link carries a state this tab
 * never issued). A state is good for one return: it is cleared whether or not it matched.
 */
export const takeOAuthState = (
  provider: OAuthProvider,
  returned: string | null | undefined,
  storage: OAuthStateStorage | null = sessionStateStorage(),
): boolean => {
  if (!storage) return false;
  try {
    const expected = storage.getItem(keyFor(provider));
    storage.removeItem(keyFor(provider));
    return !!expected && !!returned && expected === returned;
  } catch {
    return false;
  }
};

/**
 * The separator between the nonce and the landing path in a `state`: the provider echoes
 * the state back untouched in the callback URL, which is the only thing that survives
 * the round trip without storage.
 */
const STATE_SEPARATOR = "|";

/** A `state` that also carries where to land afterwards. */
export const withNextInState = (state: string, next: string): string =>
  `${state}${STATE_SEPARATOR}${encodeURIComponent(next)}`;

export const nextFromState = (
  state: string | null | undefined,
): string | undefined => {
  if (!state) return undefined;
  const separator = state.indexOf(STATE_SEPARATOR);
  if (separator === -1) return undefined;
  try {
    return decodeURIComponent(state.slice(separator + 1));
  } catch {
    return undefined;
  }
};
